import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PasswordInput } from '@weco/ui'
import { peopleAuthUrl } from '../config/peopleApi'
import { useShellStore } from '../store'

export const ChangePasswordForm: React.FC = () => {
  const { t } = useTranslation()
  const { getValidAccessToken, logout } = useShellStore()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (newPassword.length < 8) {
      setError(t('settings.password.tooShort'))
      return
    }

    if (newPassword === currentPassword) {
      setError(t('settings.password.sameAsCurrent'))
      return
    }

    if (newPassword !== confirmNewPassword) {
      setError(t('settings.password.mismatch'))
      return
    }

    setLoading(true)
    try {
      const access = await getValidAccessToken()
      if (!access) {
        setError(t('settings.password.sessionExpired'))
        return
      }

      const res = await fetch(peopleAuthUrl('change-password/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${access}`,
        },
        body: JSON.stringify({
          old_password: currentPassword,
          new_password: newPassword,
        }),
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        const detail = data?.detail
        const msg =
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail
                  .map(
                    (d: { string?: string }) => d?.string || JSON.stringify(d)
                  )
                  .join(' ')
              : data?.new_password
                ? Array.isArray(data.new_password)
                  ? data.new_password.join(' ')
                  : String(data.new_password)
                : t('settings.password.failed')
        throw new Error(msg)
      }

      setCurrentPassword('')
      setNewPassword('')
      setConfirmNewPassword('')
      setSuccess(t('settings.password.success'))
      setTimeout(() => logout(), 2000)
    } catch (err: unknown) {
      const anyErr = err as { name?: string; message?: string }
      if (
        anyErr?.name === 'TypeError' &&
        String(anyErr?.message).includes('fetch')
      ) {
        setError(t('settings.password.unreachable'))
      } else {
        setError(anyErr.message || t('settings.password.failed'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className='space-y-4'>
      <div>
        <label
          className='block text-xs font-medium mb-1.5'
          style={{ color: 'var(--text-secondary)' }}
        >
          {t('settings.password.current')}
        </label>
        <PasswordInput
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          autoComplete='current-password'
          required
        />
      </div>

      <div>
        <label
          className='block text-xs font-medium mb-1.5'
          style={{ color: 'var(--text-secondary)' }}
        >
          {t('settings.password.new')}
        </label>
        <PasswordInput
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete='new-password'
          required
        />
      </div>

      <div>
        <label
          className='block text-xs font-medium mb-1.5'
          style={{ color: 'var(--text-secondary)' }}
        >
          {t('settings.password.confirm')}
        </label>
        <PasswordInput
          value={confirmNewPassword}
          onChange={(e) => setConfirmNewPassword(e.target.value)}
          autoComplete='new-password'
          required
        />
      </div>

      {error && (
        <p
          className='text-xs px-3 py-2 rounded-lg'
          style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444' }}
        >
          {error}
        </p>
      )}
      {success && (
        <p
          className='text-xs px-3 py-2 rounded-lg'
          style={{ background: 'rgba(34,197,94,0.12)', color: '#16A34A' }}
        >
          {success}
        </p>
      )}

      <button
        type='submit'
        disabled={loading}
        className='px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60'
        style={{ background: 'var(--accent)', color: 'var(--color-primary-foreground, #000)', border: 'none' }}
      >
        {loading ? t('settings.password.updating') : t('settings.password.submit')}
      </button>
    </form>
  )
}
